import { type AssetExposure, isOptionSymbol } from '@/features/home/lib/home-exposure'

/**
 * "Coming up for your positions": the dated things that will move or settle what the user holds —
 * their options and futures expiring, the next funding settlement on each perp they hold, the
 * weekly options expiry on their coins (with max pain), token unlocks and project events on
 * their coins, the next Fed decision and the high-impact economic releases.
 * Pure (tested in __tests__/home-insights.test.ts).
 */

export type UpcomingKind =
  | 'expiry'
  | 'weeklyExpiry'
  | 'funding'
  | 'unlock'
  | 'event'
  | 'fomc'
  | 'macro'

export interface UpcomingEvent {
  id: string
  kind: UpcomingKind
  at: number
  /** What it's about: a symbol, a coin, a currency or the Fed. */
  subject: string
  /** Extra numbers for the message (max pain strike, impact level). */
  params?: Record<string, string | number>
}

const MONTHS: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
}

/** Deribit-style expiry in a symbol ("BTC-27MAR26", "ETH-3OCT26-2500-C"), at 08:00 UTC. */
export function expiryOfSymbol(symbol: string): number | null {
  const match = /-(\d{1,2})([A-Z]{3})(\d{2})(?:-|$)/i.exec(symbol)
  if (!match) return null
  const month = MONTHS[match[2].toUpperCase()]
  if (month === undefined) return null
  return Date.UTC(2000 + Number(match[3]), month, Number(match[1]), 8)
}

/** The next Friday 08:00 UTC — Deribit's weekly options expiry — strictly after `now`. */
export function nextWeeklyExpiry(now: number): number {
  const date = new Date(now)
  const friday = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 8)
  const daysAhead = (5 - date.getUTCDay() + 7) % 7
  const candidate = friday + daysAhead * 86_400_000
  return candidate > now ? candidate : candidate + 7 * 86_400_000
}

export interface UpcomingInput {
  exposures: readonly AssetExposure[]
  now: number
  /** Next funding time (epoch ms) per held perp symbol, from live tickers. */
  nextFunding?: Readonly<Record<string, number>>
  /** Max pain strike for the next expiry, per coin. */
  maxPain?: Readonly<Record<string, number>>
  /** Coins Deribit lists options on (they get the weekly expiry). */
  optionAssets?: ReadonlySet<string>
  fomc?: { at: number; outcome: string } | null
  macro?: readonly { id: string; at: number; name: string; currency: string; impact: number }[]
  /** Token unlocks (Tokenomist); only the user's coins are kept. */
  unlocks?: readonly {
    id: string
    at: number
    asset: string
    valueUsd: number
    /** Unlock value as a percent of market cap, when known. */
    pctOfMcap: number | null
    approximate: boolean
  }[]
  /** Project events (CoinMarketCal); kept when they name one of the user's coins. */
  projectEvents?: readonly {
    id: string
    at: number
    assets: readonly string[]
    title: string
    estimated: boolean
  }[]
  /** How far ahead to look. */
  horizonMs?: number
  limit?: number
}

export function buildUpcoming(input: UpcomingInput): UpcomingEvent[] {
  const { exposures, now } = input
  const horizon = now + (input.horizonMs ?? 8 * 86_400_000)
  const events: UpcomingEvent[] = []
  const add = (event: UpcomingEvent) => {
    if (event.at > now && event.at <= horizon && !events.some((e) => e.id === event.id)) {
      events.push(event)
    }
  }

  for (const exposure of exposures) {
    for (const risk of exposure.positions) {
      const symbol = risk.position.symbol
      const at = expiryOfSymbol(symbol)
      if (at !== null) {
        add({
          id: `expiry:${symbol}`,
          kind: 'expiry',
          at,
          subject: symbol,
          params: { type: isOptionSymbol(symbol) ? 'option' : 'future' },
        })
      }
      const funding = input.nextFunding?.[symbol]
      if (funding) add({ id: `funding:${symbol}`, kind: 'funding', at: funding, subject: symbol })
    }
    if (exposure.positions.length > 0 && input.optionAssets?.has(exposure.asset)) {
      const pain = input.maxPain?.[exposure.asset]
      add({
        id: `weeklyExpiry:${exposure.asset}`,
        kind: 'weeklyExpiry',
        at: nextWeeklyExpiry(now),
        subject: exposure.asset,
        params: pain ? { maxPain: pain } : undefined,
      })
    }
  }

  const held = new Set(
    exposures.filter((exposure) => exposure.positions.length > 0).map((e) => e.asset)
  )
  for (const unlock of input.unlocks ?? []) {
    if (!held.has(unlock.asset)) continue
    add({
      id: `unlock:${unlock.id}`,
      kind: 'unlock',
      at: unlock.at,
      subject: unlock.asset,
      params: {
        value: unlock.valueUsd,
        ...(unlock.pctOfMcap !== null ? { pct: unlock.pctOfMcap } : {}),
        approximate: unlock.approximate ? 'yes' : 'no',
      },
    })
  }
  for (const event of input.projectEvents ?? []) {
    const asset = event.assets.find((symbol) => held.has(symbol.toUpperCase()))
    if (!asset) continue
    add({
      id: `event:${event.id}`,
      kind: 'event',
      at: event.at,
      subject: asset.toUpperCase(),
      params: { title: event.title, estimated: event.estimated ? 'yes' : 'no' },
    })
  }

  if (input.fomc) {
    add({
      id: 'fomc',
      kind: 'fomc',
      at: input.fomc.at,
      subject: 'FOMC',
      params: { outcome: input.fomc.outcome },
    })
  }
  for (const event of input.macro ?? []) {
    add({
      id: `macro:${event.id}`,
      kind: 'macro',
      at: event.at,
      subject: event.currency,
      params: { name: event.name, impact: event.impact },
    })
  }

  return events.sort((a, b) => a.at - b.at).slice(0, input.limit ?? 8)
}
